import React, { useState, useEffect, useCallback } from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { request } from '../../../api/request.tsx';
import UpdateStatusContact from './UpdateStatusContact.tsx';
import DetailContact from './DetailContact.tsx';
import Pagination from '../../../component/Pagination/index.tsx';
import { useNavigate } from 'react-router-dom';

interface ContactData {
  id: string;
  status: string;
  isSendConfirmMail: boolean;
  formType: string;
  fullName: string;
  phoneNumber: string;
  email: string;
  consent: boolean;
  payload: {
    messageContent: string;
  };
  createdAt: string;
  updatedAt: string;
}

// interface ContactResponse {
//   results: ContactData[];
//   totalPages: number;
//   totalCount: number;
// }

const Contact = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<ContactData[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [filters, setFilters] = useState({
    pending: true,
    contacted: false,
    resolved: false,
    spam: false
  });
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [selectedContactStatus, setSelectedContactStatus] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactData | null>(null);

  const fetchData = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit: 20
      };

      if (search) {
        params.phoneNumber_like = search;
      }

      const activeFilters: string[] = [];
      if (filters.pending) activeFilters.push('pending');
      if (filters.contacted) activeFilters.push('contacted');
      if (filters.resolved) activeFilters.push('resolved');
      if (filters.spam) activeFilters.push('spam');
      const statusFilter = activeFilters.join(',');
      
      if (statusFilter) {
        params.status_is = statusFilter;
      }

      const response = await request('contact', null, 'GET', params);
      
      if (response?.data) {
        setData(response.data.results || []);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setData([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setData([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Initial load
  useEffect(() => {
    fetchData(1, '');
  }, []);

  // Handle filter and search changes - reset to page 1
  useEffect(() => {
    setCurrentPage(1);
    fetchData(1, debouncedSearchValue);
  }, [filters, debouncedSearchValue, fetchData]);

  const handleSearch = () => {
    setCurrentPage(1);
    setDebouncedSearchValue(searchValue);
  };

  const handlePageChange = ({ selected }: { selected: number }) => {
    const newPage = selected + 1;
    setCurrentPage(newPage);
    fetchData(newPage, debouncedSearchValue);
  };

  const handleFilterChange = (filterName: keyof typeof filters) => {
    setFilters({
      pending: filterName === 'pending',
      contacted: filterName === 'contacted',
      resolved: filterName === 'resolved',
      spam: filterName === 'spam'
    });
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN');
  };

  const formatPhoneNumber = (phone: string) => {
    if (phone.startsWith('84')) {
      return phone.substring(2);
    }
    return phone;
  };

  const getInvestmentCost = (contact: ContactData) => {
    return '---';
  };

  const handleUpdateStatus = (contactId: string, currentStatus: string) => {
    setSelectedContactId(contactId);
    setSelectedContactStatus(currentStatus);
    setShowUpdateModal(true);
  };

  const handleStatusUpdated = () => {
    fetchData(currentPage, debouncedSearchValue);
    setShowUpdateModal(false);
  };

  const handleCloseModal = () => {
    setShowUpdateModal(false);
    setSelectedContactId('');
    setSelectedContactStatus('');
  };

  const handleShowDetail = (contact: ContactData) => {
    setSelectedContact(contact);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedContact(null);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Title */}
      <h1 className="text-2xl text-center text-[pink] font-bold">
        DANH SÁCH NGƯỜI LIÊN HỆ
      </h1>

      {/* Search Bar */}
      <div className="flex justify-center w-full my-6">
        <div
          className="
        flex 
        items-center 
        border-[1.5px] 
        md:w-[50%]
        w-full
        rounded-lg
        pl-2
        border-[pink]
        mb-3
        "
        >
          <input
            className="flex w-full px-2 py-2 outline-none "
            placeholder="Tìm kiếm số điện thoại"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button
            onClick={handleSearch}
            className="bg-[pink] px-3 h-full rounded-tr-lg rounded-br-lg"
          >
            <AiOutlineSearch size={26} />
          </button>
        </div>
      </div>

      {/* Filter Checkboxes */}
      <div className="flex flex-wrap gap-6 mb-6">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={filters.pending}
            onChange={() => handleFilterChange('pending')}
            className="w-4 h-4 border border-gray-300 rounded mr-2"
          />
          <span className={`${filters.pending ? 'text-gray-700' : 'text-gray-400'}`}>
            Đang chờ
          </span>
        </label>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={filters.contacted}
            onChange={() => handleFilterChange('contacted')}
            className="w-4 h-4 border border-gray-300 rounded mr-2"
          />
          <span className={`${filters.contacted ? 'text-gray-700' : 'text-gray-400'}`}>
            Đã liên hệ
          </span>
        </label>
                 <label className="flex items-center cursor-pointer">
           <input
             type="checkbox"
             checked={filters.resolved}
             onChange={() => handleFilterChange('resolved')}
             className="w-4 h-4 border border-gray-300 rounded mr-2"
           />
           <span className={`${filters.resolved ? 'text-gray-700' : 'text-gray-400'}`}>
             Đã xử lý
           </span>
         </label>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={filters.spam}
            onChange={() => handleFilterChange('spam')}
            className="w-4 h-4 border border-gray-300 rounded mr-2"
          />
          <span className={`${filters.spam ? 'text-gray-700' : 'text-gray-400'}`}>
            Spam
          </span>
        </label>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Email</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Số điện thoại</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Tên</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Kinh phí đầu tư</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Ngày liên hệ</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Hành động</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-300"></div>
                    <span className="ml-2">Đang tải...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  Không có dữ liệu
                </td>
              </tr>
            ) : (
              data.map((contact) => (
                <tr key={contact.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-sm text-gray-700">{contact.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {formatPhoneNumber(contact.phoneNumber)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{contact.fullName}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {getInvestmentCost(contact)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {formatDate(contact.createdAt)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleUpdateStatus(contact.id, contact.status)}
                        className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600 transition-colors"
                      >
                        Cập nhập
                      </button>
                      <button 
                        onClick={() => handleShowDetail(contact)}
                        className="bg-pink-300 text-white px-3 py-1 rounded text-xs hover:bg-pink-400 transition-colors"
                      >
                        Chi tiết
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

       {/* Pagination */}
       <Pagination
         pageCount={totalPages}
         onPageChange={handlePageChange}
         currentPage={currentPage}
         marginPagesDisplayed={2}
         pageRangeDisplayed={2}
       />

      {/* Update Status Modal */}
      {showUpdateModal && (
        <UpdateStatusContact
          formId={selectedContactId}
          currentStatus={selectedContactStatus}
          onStatusUpdated={handleStatusUpdated}
          onClose={handleCloseModal}
        />
      )}

      {/* Detail Contact Modal */}
      {showDetailModal && selectedContact && (
        <DetailContact
          contactData={selectedContact}
          onClose={handleCloseDetailModal}
        />
      )}
    </div>
  );
};

export default Contact;